import React, { useState, useEffect } from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { marketingApi } from '../../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Mail, Users, FileText, Eye } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  { id: 1, label: 'Basic Info', icon: FileText },
  { id: 2, label: 'Template', icon: Mail },
  { id: 3, label: 'Segment', icon: Users },
  { id: 4, label: 'Review', icon: Eye },
];

export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Try to load draft from localStorage
    const saved = localStorage.getItem('campaign_draft');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {
          name: '',
          channel: 'sms',
          template_id: '',
          segment_id: '',
        };
      }
    }
    return {
      name: '',
      channel: 'sms',
      template_id: '',
      segment_id: '',
    };
  });
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);

  // Save draft to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem('campaign_draft', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    // Load templates
    marketingApi.getTemplates()
      .then(res => setTemplates(res.data.templates || res.data || []))
      .catch(() => toast.error('Failed to load templates'));
    
    // Load segments
    marketingApi.getSegments()
      .then(res => setSegments(res.data.segments || res.data || []))
      .catch(() => toast.error('Failed to load segments'));
  }, []);

  useEffect(() => {
    if (formData.template_id && templates.length > 0) {
      const template = templates.find(t => t._id === formData.template_id);
      setSelectedTemplate(template);
    }
  }, [formData.template_id, templates]);

  useEffect(() => {
    if (formData.segment_id && segments.length > 0) {
      const segment = segments.find(s => s._id === formData.segment_id);
      setSelectedSegment(segment);
    }
  }, [formData.segment_id, segments]);

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.name) {
        toast.error('Campaign name is required');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.template_id) {
        toast.error('Please select a template');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.segment_id) {
        toast.error('Please select a segment');
        return;
      }
    }
    setCurrentStep(Math.min(currentStep + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const handleCreate = () => {
    setLoading(true);
    marketingApi.createCampaign(formData)
      .then(res => {
        const campaignId = res.data._id || res.data.id;
        // Clear draft on successful creation
        localStorage.removeItem('campaign_draft');
        toast.success('Campaign created successfully');
        navigate(`/admin/marketing/campaigns/${campaignId}`);
      })
      .catch(() => toast.error('Failed to create campaign'))
      .finally(() => setLoading(false));
  };

  const renderStepIndicator = () => {
    const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100;
    return (
      <div className="mb-8">
        <Progress value={progress} className="h-1 mb-6" />
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const isActive = currentStep === step.id;
            const isDone = currentStep > step.id;
            const Icon = step.icon;
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs mb-2 transition-colors ${
                    isActive 
                      ? 'border-[rgba(212,168,67,0.45)] bg-[rgba(212,168,67,0.12)] text-[#D4A843]'
                      : isDone
                      ? 'border-[rgba(61,220,151,0.35)] bg-[rgba(61,220,151,0.10)] text-[#BFF3D2]'
                      : 'border-[rgba(255,255,255,0.14)] text-[rgba(242,242,242,0.58)]'
                  }`}>
                    {isDone ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <p className={`text-xs ${isActive ? 'text-[#F2F2F2]' : 'text-[#6F6F6F]'}`}>{step.label}</p>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[rgba(61,220,151,0.35)]' : 'bg-[rgba(255,255,255,0.12)]'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <Card className="bg-[#111111] border-[#2A2A2A]">
      <CardHeader>
        <CardTitle className="text-[#F2F2F2] font-['EB_Garamond']">Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-[#B9B9B9] text-sm">Campaign Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Weekend Event Promotion"
            className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
            data-testid="campaign-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#B9B9B9] text-sm">Channel</Label>
          <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
            <SelectTrigger className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]" data-testid="campaign-channel-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => {
    const filteredTemplates = templates.filter(t => t.channel === formData.channel);
    return (
      <Card className="bg-[#111111] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['EB_Garamond']">Select Template</CardTitle>
          <p className="text-xs text-[#6F6F6F]">Choose a template for {formData.channel?.toUpperCase()} messages</p>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-[#B9B9B9] text-sm mb-3">No {formData.channel?.toUpperCase()} templates available</p>
              <Button
                onClick={() => navigate('/admin/marketing/templates/new')}
                variant="outline"
                className="bg-transparent border-[#2A2A2A] text-[#B9B9B9]"
              >Create a template first</Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {filteredTemplates.map(template => (
                <div
                  key={template._id}
                  onClick={() => setFormData({ ...formData, template_id: template._id })}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    formData.template_id === template._id
                      ? 'border-[#D4A843] bg-[rgba(212,168,67,0.08)]'
                      : 'border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.22)]'
                  }`}
                  data-testid="template-option"
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm text-[#F2F2F2] font-medium">{template.name}</p>
                    {formData.template_id === template._id && (
                      <Check size={16} className="text-[#D4A843] flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-[#6F6F6F] mb-2">{template.template_body?.substring(0, 100)}{template.template_body?.length > 100 ? '...' : ''}</p>
                  {template.variables_detected?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {template.variables_detected.map(v => (
                        <span key={v} className="inline-block px-1.5 py-0.5 rounded bg-[rgba(212,168,67,0.14)] border border-[rgba(212,168,67,0.28)] text-[10px] font-mono text-[#D4A843]">
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderStep3 = () => (
    <Card className="bg-[#111111] border-[#2A2A2A]">
      <CardHeader>
        <CardTitle className="text-[#F2F2F2] font-['EB_Garamond']">Select Audience Segment</CardTitle>
        <p className="text-xs text-[#6F6F6F]">Choose who will receive this campaign</p>
      </CardHeader>
      <CardContent>
        {segments.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-[#B9B9B9] text-sm">No segments available. Using default "All Customers" segment.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {segments.map(segment => (
              <div
                key={segment._id}
                onClick={() => setFormData({ ...formData, segment_id: segment._id })}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  formData.segment_id === segment._id
                    ? 'border-[#D4A843] bg-[rgba(212,168,67,0.08)]'
                    : 'border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.22)]'
                }`}
                data-testid="segment-option"
              >
                <div className="flex items-start justify-between mb-1">
                  <p className="text-sm text-[#F2F2F2] font-medium">{segment.name}</p>
                  {formData.segment_id === segment._id && (
                    <Check size={16} className="text-[#D4A843] flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-[#6F6F6F] mb-2">
                  {segment.is_dynamic ? 'Dynamic segment' : 'Static segment'} • Est. {segment.estimated_count || 0} recipients
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <Card className="bg-[#111111] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['EB_Garamond']">Review Campaign</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs text-[#B9B9B9] mb-1">Campaign Name</p>
            <p className="text-sm text-[#F2F2F2]">{formData.name}</p>
          </div>
          <Separator className="bg-[#2A2A2A]" />
          <div>
            <p className="text-xs text-[#B9B9B9] mb-1">Channel</p>
            <Badge className="bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]">
              {formData.channel?.toUpperCase()}
            </Badge>
          </div>
          <Separator className="bg-[#2A2A2A]" />
          <div>
            <p className="text-xs text-[#B9B9B9] mb-2">Template</p>
            {selectedTemplate ? (
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.12)] rounded-lg">
                <p className="text-sm text-[#F2F2F2] font-medium mb-1">{selectedTemplate.name}</p>
                <p className="text-xs text-[#6F6F6F]">{selectedTemplate.template_body}</p>
              </div>
            ) : (
              <p className="text-sm text-[#6F6F6F]">No template selected</p>
            )}
          </div>
          <Separator className="bg-[#2A2A2A]" />
          <div>
            <p className="text-xs text-[#B9B9B9] mb-2">Audience</p>
            {selectedSegment ? (
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.12)] rounded-lg">
                <p className="text-sm text-[#F2F2F2] font-medium mb-1">{selectedSegment.name}</p>
                <p className="text-xs text-[#6F6F6F]">Estimated recipients: {selectedSegment.estimated_count || 0}</p>
              </div>
            ) : (
              <p className="text-sm text-[#6F6F6F]">No segment selected</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <AdminShell title="New Campaign">
      <div className="max-w-3xl mx-auto">
        {renderStepIndicator()}
        
        <div className="min-h-[400px] mb-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-[#2A2A2A] pt-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => currentStep === 1 ? navigate('/admin/marketing/campaigns') : handleBack()}
            className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40"
          >
            <ArrowLeft size={16} className="mr-2" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          {currentStep < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
              data-testid="campaign-wizard-next-button"
            >
              Next
              <ArrowRight size={16} />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={loading}
              className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
              data-testid="campaign-wizard-create-button"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </Button>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
